import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import { useVaccinationHistoryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationHistoryListAPI";
import { useVaccinationSummaryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationSummaryListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import { AiOutlineEye } from "react-icons/ai";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router";
import OCRmodalComponent from "../OCRmodalComponent";
import IteamDeleteComponent from "../IteamDeleteComponent";
import FileViewComponent from "../FileViewComponent";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const VaccinationDeworming = () => {
  const navigate = useNavigate();
  const { NO_CART } = useImageConstants();
  const { veticDateFormat } = useUtils();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { petDetails, customerId, petId, vaccinationData, vaccinationSummaryData } =
    routeState.state as ICustomerProfileState;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemDelete, setItemDelete] = useState(false);
  const [fileView, setFileView] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [vaccination_id, setVaccination_id] = useState(null);
  const [vaccination_file, setVaccination_file] = useState(null);
  const [mode, setMode] = useState(null);
  const isVaccinationLoader = useLoader("isvaccinationTableLoading");
  const { execute: fetchVaccinationHistoryList } = useVaccinationHistoryListAPI();
  const { execute: fetchVaccinationSummaryList } = useVaccinationSummaryListAPI();

  useEffect(() => {
    fetchVaccinationHistoryList(petId, customerId, "deworming");
    fetchVaccinationSummaryList(petId, customerId, "deworming");
  }, []);

  const handleAddPetClick = () => {
    navigate(`/app/customer/profile/customer-details/add-pet/${customerId}`);
  };

  return (
    <div className="pb-5 sm:pb-0">
      <h2 className="text-sm font-semibold text-gray-900">Deworming Status</h2>
      <div>
        {isVaccinationLoader ? (
          <TableSkeletonComponent columnLength={5} bodyLength={6} />
        ) : (
          <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px] flow-root relative rounded-xl bg-[#F9FAFB] mt-3">
            <div className="w-full">
              <div className="inline-block min-w-full align-middle">
                {Array.isArray(vaccinationSummaryData) && vaccinationSummaryData.length > 0 ? (
                  <table className="w-full min-w-[680px] sm:min-w-[760px] md:min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                    <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                      <tr>
                        <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                          Name
                        </th>
                        <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                          Last Given Date
                        </th>
                        <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                          Next Due Date
                        </th>
                        <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                          Next Due Vaccine
                        </th>
                        <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {vaccinationSummaryData?.map((vac, index) => {
                        const isFirst = index === 0;
                        const isLast = index === vaccinationSummaryData.length - 1;
                        const isSingle = vaccinationSummaryData.length === 1;
                        return (
                          <tr
                            key={index}
                            className={`
                              bg-white
                              ${isSingle ? "rounded-xl" : ""}
                              ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                              ${isLast && !isSingle ? "rounded-b-xl" : ""}
                            `}
                          >
                            <td
                              className={`
                                px-2 py-3 whitespace-nowrap text-xs font-medium text-black
                                ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                              `}
                            >
                              {vac?.vaccine_name}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-xs text-black">
                              {veticDateFormat(vac?.given_date) || "--"}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-xs text-black">
                              {veticDateFormat(vac?.due_date) || "--"}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-xs text-black">
                              {vac?.next_due_vaccine_name}
                            </td>
                            <td
                              className={`
                                px-2 py-3 whitespace-nowrap text-xs
                                ${vac?.status === "Overdue" ? "text-red-500 font-medium" : "text-black"}
                                ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                              `}
                            >
                              {vac?.status || "NA"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex min-h-[240px] sm:min-h-[300px] items-center justify-center rounded-xl bg-white">
                    <div className="text-center">
                      <NoItemFoundComponent message="" description="" imageURL={NO_CART} InputClassName="my-0 h-full" />
                      <p className="text-xs font-medium mt-24">
                        {petDetails ? "No Deworming records found." : "No pets found to add Deworming records."}
                      </p>
                      {!petDetails && (
                        <div className="mt-7">
                          <input
                            type="submit"
                            onClick={handleAddPetClick}
                            className="bg-[var(--color-primary)] px-6 py-2 border-transparent rounded-md text-xs text-white cursor-pointer"
                            value={"+ Add Pet"}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="border rounded-xl mt-5 bg-white overflow-hidden">
        <Disclosure defaultOpen={false}>
          {({ open }) => (
            <>
              <Disclosure.Button className="px-2 py-3 items-center flex w-full justify-between rounded-lg bg-[#808DA01A] text-left text-xs font-semibold text-gray-900 focus:outline-none">
                <div className="flex flex-row gap-16 items-center">
                  <div className="flex flex-col gap-1">
                    <span>Deworming History</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-normal underline">
                  {open ? "Hide Details" : "Show Details"}
                  <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-4 w-4 `} />
                </div>
              </Disclosure.Button>
              <Disclosure.Panel className="text-sm text-gray-500 bg-white">
                <div className="p-1 py-2">
                  {isVaccinationLoader ? (
                    <TableSkeletonComponent columnLength={5} bodyLength={6} />
                  ) : (
                    <div
                      className={`scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px] flow-root relative rounded-xl ${
                        vaccinationSummaryData?.length ? "bg-[#F9FAFB]" : ""
                      }`}
                    >
                      <div className="w-full">
                        <div className="inline-block min-w-full align-middle">
                          {Array.isArray(vaccinationData) && vaccinationData.length > 0 ? (
                            <table className="w-full min-w-[680px] sm:min-w-[760px] md:min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                              <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                                <tr>
                                  <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                                    Given Date
                                  </th>
                                  <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                                    Name
                                  </th>
                                  <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                                    Uploaded By
                                  </th>
                                  <th className="p-2 py-3 text-center text-xs whitespace-nowrap font-medium text-black tracking-wider">
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {vaccinationData?.map((vac, index) => {
                                  const isFirst = index === 0;
                                  const isLast = index === vaccinationData.length - 1;
                                  const isSingle = vaccinationData.length === 1;
                                  return (
                                    <tr
                                      key={index}
                                      className={`
                                        bg-white
                                        ${isSingle ? "rounded-xl" : ""}
                                        ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                                        ${isLast && !isSingle ? "rounded-b-xl" : ""}
                                      `}
                                    >
                                      <td
                                        className={`
                                          whitespace-nowrap px-2 py-3 text-xs text-black
                                          ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                          ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                          ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                                        `}
                                      >
                                        {veticDateFormat(vac?.given_date) || "--"}
                                      </td>
                                      <td className="whitespace-nowrap px-2 py-3 text-xs font-medium text-black">
                                        {vac?.vaccine_name}
                                      </td>
                                      <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                                        <div className="flex flex-col gap-1">
                                          <span>{vac?.created_by}</span>
                                        </div>
                                        <p className="text-xs">{veticDateFormat(vac?.created_at) || "NA"}</p>
                                      </td>
                                      <td
                                        className={`
                                          whitespace-nowrap px-2 py-3 text-center text-xs text-black
                                          ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                          ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                          ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                                        `}
                                      >
                                        {vac?.file_url ? (
                                          <div className="flex gap-7 justify-center">
                                            {vac?.is_deletable ? (
                                              <div
                                                onClick={() => {
                                                  setOpen(true);
                                                  setVaccination_id(vac?.vaccination_id);
                                                  setVaccination_file(vac?.file_url);
                                                  setMode("EDIT");
                                                }}
                                                className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                                              >
                                                <MdEdit className="w-5 h-5" />
                                              </div>
                                            ) : (
                                              <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                                -
                                              </div>
                                            )}
                                            <div
                                              onClick={() => {
                                                setFileView(true);
                                                setVaccination_file(vac?.file_url);
                                              }}
                                              className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                                            >
                                              <AiOutlineEye className="w-5 h-5" />
                                            </div>
                                            {vac?.is_deletable ? (
                                              <div
                                                onClick={() => {
                                                  setItemDelete(true);
                                                  setVaccination_id(vac?.vaccination_id);
                                                  setVaccination_file(vac?.file_url);
                                                }}
                                                className="flex items-center gap-2 cursor-pointer hover:text-red-600"
                                              >
                                                <MdDeleteForever color="gray" className="w-5 h-5" />
                                              </div>
                                            ) : (
                                              <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                                -
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="flex gap-10 justify-center">
                                            <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                              -
                                            </div>
                                            <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600">
                                              -
                                            </div>
                                            <div className="flex items-center gap-2 cursor-pointer hover:text-red-600">
                                              -
                                            </div>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <div className="flex min-h-[240px] sm:min-h-[300px] items-center justify-center rounded-xl bg-white mt-5">
                              <div className="text-center">
                                <NoItemFoundComponent
                                  message=""
                                  description=""
                                  imageURL={NO_CART}
                                  InputClassName="my-0 h-full"
                                />
                                <p className="text-xs font-medium mt-24">
                                  {petDetails
                                    ? "No Deworming records found."
                                    : "No pets found to add Deworming records."}
                                </p>
                                {!petDetails && (
                                  <div className="mt-7">
                                    <input
                                      type="submit"
                                      onClick={handleAddPetClick}
                                      className="bg-[var(--color-primary)] px-6 py-2 border-transparent rounded-md text-xs text-white cursor-pointer"
                                      value={"+ Add Pet"}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
      {open && (
        <OCRmodalComponent
          vaccination_id={vaccination_id}
          vaccination_file={vaccination_file}
          mode={mode}
          setMode={setMode}
          open={open}
          setOpen={setOpen}
          loading={loading}
          setLoading={setLoading}
          fileData={fileData}
          setFileData={setFileData}
        />
      )}
      {itemDelete && (
        <IteamDeleteComponent
          open={itemDelete}
          setOpen={setItemDelete}
          vaccination_id={vaccination_id}
          vaccination_file={vaccination_file}
        />
      )}
      {fileView && <FileViewComponent open={fileView} setOpen={setFileView} vaccination_file={vaccination_file} />}
    </div>
  );
};
export default VaccinationDeworming;
